import React, {useEffect, useState, useMemo} from "react";
import {Card, Row, Col, Table, Typography, DatePicker, Select, Spin, Empty, Tag} from "antd";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
} from "recharts";
import api from "../../api";
import dayjs from "dayjs";
import {AnalyticsResponse} from "../../types";

const {Title} = Typography;
const {RangePicker} = DatePicker;
const {Option} = Select;

interface RawTopPlayer {
    id: number;
    first_name: string;
    last_name: string;
    total_buyin: number | null;
    total_win_loss: number | null;
    total_pnl: number | null;
    total_trips: number;
}

interface RawTopAgent {
    id: number;
    first_name: string;
    last_name: string;
    total_buyin: number | null;
    total_win_loss: number | null;
    total_pnl: number | null;
    total_players: number;
}

interface TopPlayer {
    id: number;
    first_name: string;
    last_name: string;
    total_buyin: number;
    total_win_loss: number;
    total_pnl: number;
    total_trips: number;
}

interface TopAgent {
    id: number;
    first_name: string;
    last_name: string;
    total_buyin: number;
    total_win_loss: number;
    total_pnl: number;
    total_players: number;
}

const AnalyticsPage: React.FC = () => {
    const [rawData, setRawData] = useState<AnalyticsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [playerSortKey, setPlayerSortKey] = useState<"total_buyin" | "total_pnl" | "total_win_loss">("total_pnl");
    const [agentSortKey, setAgentSortKey] = useState<"total_buyin" | "total_pnl" | "total_win_loss">("total_pnl");
    const [pagination_players, setPagination_players] = useState({current: 1, pageSize: 10});
    const [pagination_agents, setPagination_agents] = useState({current: 1, pageSize: 10});

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const params = dateRange ? {start_date: dateRange[0], end_date: dateRange[1]} : {};
            const response = await api.get<AnalyticsResponse>("/daia/analytics/", {params});
            setRawData(response.data);
        } catch (error) {
            console.error("Error fetching analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    const data = useMemo(() => {
        if (!rawData) return null;

        const mergedPlayers: TopPlayer[] = (rawData.top_players as RawTopPlayer[])
            .filter((p) => (p.total_buyin ?? 0) > 0 || (p.total_trips ?? 0) > 0)
            .map((p) => ({
                id: p.id,
                first_name: p.first_name,
                last_name: p.last_name,
                total_buyin: p.total_buyin ?? 0,
                total_win_loss: p.total_win_loss ?? 0,
                total_pnl: p.total_pnl ?? 0,
                total_trips: p.total_trips,
            }))
            .sort((a, b) => (b[playerSortKey] ?? 0) - (a[playerSortKey] ?? 0));

        const mergedAgents: TopAgent[] = (rawData.top_agents as RawTopAgent[])
            .filter((a) => (a.total_buyin ?? 0) > 0 || (a.total_players ?? 0) > 0)
            .map((a) => {
                const loss = rawData.top_agents_loss.find((l) => l.id === a.id);
                return {
                    id: a.id,
                    first_name: a.first_name,
                    last_name: a.last_name,
                    total_buyin: a.total_buyin ?? 0,
                    total_win_loss: loss?.total_loss ?? 0,
                    total_pnl: a.total_pnl ?? 0,
                    total_players: a.total_players,
                };
            })
            .sort((a, b) => (b[agentSortKey] ?? 0) - (a[agentSortKey] ?? 0));

        return {...rawData, top_players: mergedPlayers, top_agents: mergedAgents};
    }, [rawData, playerSortKey, agentSortKey, dateRange]);

    const formattedRange =
        dateRange && dateRange[0] && dateRange[1]
            ? `${dayjs(dateRange[0]).format("YYYY-MM-DD")} → ${dayjs(dateRange[1]).format("YYYY-MM-DD")}`
            : "All Time";

    return (
        <div style={{padding: 16}}>
            <Title level={2}>Junket Analytics</Title>

            {/* Date Picker */}
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <Col xs={24} sm={12} md={8}>
                    <RangePicker
                        onChange={(dates) => {
                            if (dates) {
                                setDateRange([
                                    dates[0]?.format("YYYY-MM-DD") || "",
                                    dates[1]?.format("YYYY-MM-DD") || "",
                                ]);
                            } else {
                                setDateRange(null);
                            }
                        }}
                        style={{width: "100%"}}
                    />
                </Col>
                <Col xs={24} sm={12} md={16} style={{display: "flex", alignItems: "center"}}>
                    <Tag color="blue" style={{fontSize: 14, padding: "6px 12px"}}>
                        Showing Data: {formattedRange}
                    </Tag>
                </Col>
            </Row>

            {loading && <Spin tip="Loading analytics..."/>}

            {!loading && !data && <Empty description="No analytics data available"/>}

            {!loading && data && (
                <>
                    <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                        <Col span={24}>
                            <Card style={{backgroundColor: "#f6ffed", border: "1px solid #b7eb8f"}}>
                                <Title level={2} style={{margin: 0, color: data.total_pnl >= 0 ? "green" : "red"}}>
                                    Total P&L: ₩{data.total_pnl?.toLocaleString() ?? "0"}
                                </Title>
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                        <Col xs={24} md={8}>
                            <Card title="Total Buy-ins">₩{data.total_buyins?.toLocaleString() ?? "0"}</Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card title="Total Players">{data.total_players ?? "0"}</Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card title="Total Agents">{data.total_agents ?? "0"}</Card>
                        </Col>
                    </Row>

                    {/* Top Players */}
                    <Row justify="space-between" align="middle" style={{marginBottom: 12}}>
                        <Col>
                            <Row align="middle" gutter={8}>
                                <Col><Title level={3} style={{margin: 0}}>Top Players</Title></Col>
                                <Col>
                                    <Tag color="blue" style={{fontSize: 12, padding: "4px 10px"}}>
                                        {formattedRange}
                                    </Tag>
                                </Col>
                            </Row>
                        </Col>
                        <Col>
                            <Select value={playerSortKey} onChange={(value) => setPlayerSortKey(value)}
                                    style={{width: 200}}>
                                <Option value="total_buyin">Sort by Buy-in</Option>
                                <Option value="total_win_loss">Sort by Win/Loss</Option>
                                <Option value="total_pnl">Sort by P&L</Option>
                            </Select>
                        </Col>
                    </Row>

                    <Table
                        dataSource={data.top_players}
                        rowKey={(record) => `${record.id}-${record.total_win_loss}`}
                        loading={loading}
                        scroll={{x: "max-content"}}
                        columns={[
                            {
                                title: "Player",
                                key: "first_name",
                                render: (_, record) => `${record.first_name} ${record.last_name}`,
                            },
                            {
                                title: "Total Buy-in",
                                dataIndex: "total_buyin",
                                key: "total_buyin",
                                render: (value) => `₩${value?.toLocaleString() ?? "0"}`,
                            },
                            {
                                title: "Win/Loss",
                                dataIndex: "total_win_loss",
                                key: "win_loss",
                                render: (value) => {
                                    const numeric = Number(value);
                                    const display = isNaN(numeric) ? "₩0" : `₩${numeric.toLocaleString()}`;
                                    const color = numeric >= 0 ? "green" : "red";
                                    return <span style={{color}}>{display}</span>;
                                },
                            },
                            {
                                title: "P&L",
                                dataIndex: "total_pnl",
                                key: "total_pnl",
                                render: (value) => {
                                    const numeric = typeof value === "number" ? value : parseFloat(value);
                                    return (
                                        <span style={{color: numeric >= 0 ? "green" : "red"}}>
                                            ₩{!isNaN(numeric) ? numeric.toLocaleString() : "0"}
                                        </span>
                                    );
                                },
                            },
                            {
                                title: "Total Trips",
                                dataIndex: "total_trips",
                                key: "total_trips",
                                render: (value) => value ?? "0",
                            },
                        ]}
                        pagination={{
                            ...pagination_players,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                setPagination_players({current: page, pageSize});
                            },
                        }}
                    />

                    {/* Top Agents */}
                    <Row justify="space-between" align="middle" style={{marginBottom: 12}}>
                        <Col>
                            <Row align="middle" gutter={8}>
                                <Col><Title level={3} style={{margin: 0}}>Top Agents</Title></Col>
                                <Col>
                                    <Tag color="blue" style={{fontSize: 12, padding: "4px 10px"}}>
                                        {formattedRange}
                                    </Tag>
                                </Col>
                            </Row>
                        </Col>
                        <Col>
                            <Select value={agentSortKey} onChange={(value) => setAgentSortKey(value)}
                                    style={{width: 200}}>
                                <Option value="total_buyin">Sort by Buy-in</Option>
                                <Option value="total_win_loss">Sort by Win/Loss</Option>
                                <Option value="total_pnl">Sort by P&L</Option>
                            </Select>
                        </Col>
                    </Row>

                    <Table
                        dataSource={data.top_agents}
                        rowKey={(record) => `${record.id}-${record.total_buyin}`}
                        loading={loading}
                        scroll={{x: "max-content"}}
                        columns={[
                            {
                                title: "Agent",
                                key: "first_name",
                                render: (_, record) => `${record.first_name} ${record.last_name}`,
                            },
                            {
                                title: "Total Buy-in",
                                dataIndex: "total_buyin",
                                key: "total_buyin",
                                render: (value) => `₩${value?.toLocaleString() ?? "0"}`,
                            },
                            {
                                title: "Win/Loss",
                                dataIndex: "total_win_loss",
                                key: "total_win_loss",
                                render: (value) => {
                                    const numeric = typeof value === "number" ? value : parseFloat(value);
                                    return (
                                        <span style={{color: numeric >= 0 ? "green" : "red"}}>
                                            ₩{!isNaN(numeric) ? numeric.toLocaleString() : "0"}
                                        </span>
                                    );
                                },
                            },
                            {
                                title: "P&L",
                                dataIndex: "total_pnl",
                                key: "total_pnl",
                                render: (value) => {
                                    const numeric = typeof value === "number" ? value : parseFloat(value);
                                    return (
                                        <span style={{color: numeric >= 0 ? "green" : "red"}}>
                                            ₩{!isNaN(numeric) ? numeric.toLocaleString() : "0"}
                                        </span>
                                    );
                                },
                            },
                            {
                                title: "Total Players",
                                dataIndex: "total_players",
                                key: "total_players",
                                render: (value) => value ?? "N/A",
                            },
                        ]}
                        pagination={{
                            ...pagination_agents,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                setPagination_agents({current: page, pageSize});
                            },
                        }}
                    />

                    {/* Charts */}
                    <Row justify="space-between" align="middle" style={{marginBottom: "12px"}}>
                        <Col>
                            <Row align="middle" gutter={8}>
                                <Col>
                                    <Title level={3} style={{margin: 0}}>Buy-in & Win/Loss Trends</Title>
                                </Col>
                                <Col>
                                    <Tag color="blue" style={{fontSize: 12, padding: "4px 10px"}}>
                                        {formattedRange}
                                    </Tag>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data.trend_data || []}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="checkin_date"/>
                            <YAxis tickFormatter={(value) => `₩${(value / 1_000_000).toFixed(0)}M`}/>
                            <Tooltip formatter={(value: any) => `₩${(value / 1_000_000).toFixed(1)}M`}/>
                            <Line type="monotone" dataKey="daily_buyin" stroke="#1890ff" name="Daily Buy-in"/>
                            <Line type="monotone" dataKey="daily_win_loss" stroke="#ff4d4f" name="Daily Win/Loss"/>
                        </LineChart>
                    </ResponsiveContainer>

                    <Row justify="space-between" align="middle" style={{marginBottom: "12px"}}>
                        <Col>
                            <Row align="middle" gutter={8}>
                                <Col>
                                    <Title level={3} style={{margin: 0}}>Win/Loss Distribution</Title>
                                </Col>
                                <Col>
                                    <Tag color="blue" style={{fontSize: 12, padding: "4px 10px"}}>
                                        {formattedRange}
                                    </Tag>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data.top_losing_players || []}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="first_name"/>
                            <YAxis tickFormatter={(value) => `₩${(value / 1_000_000).toFixed(0)}M`}/>
                            <Tooltip formatter={(value: any) => `₩${(value / 1_000_000).toFixed(1)}M`}/>
                            <Bar dataKey="total_win_loss" fill="#ff4d4f" name="Total Loss"/>
                        </BarChart>
                    </ResponsiveContainer>
                </>
            )}
        </div>
    );
};

export default AnalyticsPage;
